import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
} from 'react-native';

import { FormCompactNeutralButton } from './FormCompactNeutralButton';
import { FormFieldOverlay } from './FormFieldOverlay';
import { colors, fontSize, fontWeight, spacing } from '../../theme';
import { copyTextToClipboard } from '../../utils/copyTextToClipboard';
import { ChevronLeft, ChevronRight, Copy } from '../icons';

import type { FieldInfoExample } from '../../types/fieldInfoPanel';

/** Grey-box height when paging. A single example hugs content up to this cap. */
export const FIELD_TEXT_ASSIST_EXAMPLE_CALLOUT_HEIGHT = 280;

export type FieldTextAssistProps = {
  visible: boolean;
  onClose: () => void;
  title: string;
  bullets?: readonly string[];
  examples: readonly FieldInfoExample[];
  examplesIntro?: string;
  onInsertExample: (example: FieldInfoExample) => boolean | void;
  canInsertExample?: (text: string) => boolean;
  alreadyInserted: (text: string) => boolean;
  insertDisabledHint?: string;
  alreadyInsertedHint?: string;
  insertActionA11yLabel?: (example: FieldInfoExample) => string;
  insertActionLabel?: string;
  closeButtonLabel?: string;
  closeAssistAccessibilityLabel?: string;
  scrimAccessibilityLabel?: string;
  onDismiss?: () => void;
  testID?: string;
};

/**
 * Field-agnostic text-assist panel: one worked example at a time.
 * Overlay chrome is {@link FormFieldOverlay}. Borderless Close (left) and
 * Neutral Insert (right) sit inline in the footer. Copy sits on the example callout.
 */
export const FieldTextAssist: React.FC<FieldTextAssistProps> = ({
  visible,
  onClose,
  title,
  bullets = [],
  examples,
  examplesIntro,
  onInsertExample,
  canInsertExample,
  alreadyInserted,
  insertDisabledHint = "There's not enough room to insert the example.",
  alreadyInsertedHint = 'Already inserted.',
  insertActionA11yLabel,
  insertActionLabel = 'Insert',
  closeButtonLabel = 'Close',
  closeAssistAccessibilityLabel = 'Close assist panel',
  scrimAccessibilityLabel = 'Dismiss assist overlay',
  onDismiss,
  testID,
}) => {
  const [pageIndex, setPageIndex] = useState(0);
  const pageCount = examples.length;
  const lastIndex = Math.max(0, pageCount - 1);
  const safeIndex = Math.min(pageIndex, lastIndex);
  const example = examples[safeIndex];
  const showPager = pageCount > 1;
  const atFirst = safeIndex <= 0;
  const atLast = safeIndex >= lastIndex;
  const isAlready = example != null ? alreadyInserted(example.text) : false;
  const noRoom =
    example != null &&
    canInsertExample != null &&
    !canInsertExample(example.text);
  const insertDisabled = isAlready || noRoom;
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountedRef = useRef(false);

  const hint = isAlready
    ? alreadyInsertedHint
    : noRoom
    ? insertDisabledHint
    : undefined;

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      if (copiedTimerRef.current != null) {
        clearTimeout(copiedTimerRef.current);
      }
    };
  }, []);

  const clearCopiedNotice = (): void => {
    setCopied(false);
    if (copiedTimerRef.current != null) {
      clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (visible) {
      setPageIndex(0);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      clearCopiedNotice();
    }
  }, [visible]);

  useEffect(() => {
    clearCopiedNotice();
  }, [safeIndex]);

  const showCopiedNotice = (): void => {
    setCopied(true);
    if (copiedTimerRef.current != null) {
      clearTimeout(copiedTimerRef.current);
    }
    copiedTimerRef.current = setTimeout(() => {
      copiedTimerRef.current = null;
      if (!unmountedRef.current) {
        setCopied(false);
      }
    }, 2500);
  };

  const handleCopy = (item: FieldInfoExample): void => {
    copyTextToClipboard(item.text)
      .then(ok => {
        if (ok && !unmountedRef.current) {
          showCopiedNotice();
        }
      })
      .catch(() => undefined);
  };

  const handleInsert = (item: FieldInfoExample): void => {
    if (alreadyInserted(item.text)) {
      return;
    }
    if (canInsertExample != null && !canInsertExample(item.text)) {
      return;
    }
    const inserted = onInsertExample(item);
    if (inserted !== false) {
      onClose();
    }
  };

  return (
    <FormFieldOverlay
      visible={visible}
      onClose={onClose}
      onDismiss={onDismiss}
      title={title}
      closeAccessibilityLabel={closeAssistAccessibilityLabel}
      scrimAccessibilityLabel={scrimAccessibilityLabel}
      closeOnEscape
      testID={testID}
      footer={
        example != null ? (
          <View style={styles.footer} testID="field-text-assist-footer">
            <View style={styles.footerRow}>
              <View
                style={styles.footerClose}
                testID="field-text-assist-close-cell"
              >
                <TouchableOpacity
                  onPress={onClose}
                  style={styles.closeButton}
                  accessibilityLabel={closeButtonLabel}
                  accessibilityRole="button"
                  activeOpacity={0.88}
                  testID="field-text-assist-close"
                >
                  <Text style={styles.closeButtonText}>{closeButtonLabel}</Text>
                </TouchableOpacity>
              </View>
              <View
                style={styles.footerInsert}
                testID={`field-text-assist-insert-${example.id}`}
              >
                <FormCompactNeutralButton
                  onPress={() => handleInsert(example)}
                  disabled={insertDisabled}
                  fullWidth
                  accessibilityLabel={
                    insertActionA11yLabel?.(example) ??
                    `Insert ${example.label} at the end`
                  }
                  testID="field-text-assist-insert"
                >
                  {insertActionLabel}
                </FormCompactNeutralButton>
              </View>
            </View>
            {hint ? (
              <Text
                style={styles.disabledHint}
                testID={`field-text-assist-insert-hint-${example.id}`}
                accessibilityLiveRegion="polite"
              >
                {hint}
              </Text>
            ) : null}
          </View>
        ) : null
      }
    >
      {bullets.map((line, i) => (
        <View
          key={`field-text-assist-b-${String(i)}`}
          style={styles.bulletRow}
          accessibilityRole="text"
        >
          {bullets.length > 1 ? (
            <Text style={styles.bulletMark}>· </Text>
          ) : null}
          <Text style={styles.bodyLine}>{line}</Text>
        </View>
      ))}
      {examplesIntro != null && examplesIntro.length > 0 ? (
        <Text
          style={styles.examplesIntro}
          testID="field-text-assist-examples-intro"
          accessibilityRole="text"
        >
          {examplesIntro}
        </Text>
      ) : null}
      {showPager ? (
        <View style={styles.pagination} testID="field-text-assist-pagination">
          <TouchableOpacity
            testID="field-text-assist-prev"
            style={[styles.pageButton, atFirst && styles.pageButtonDisabled]}
            onPress={() => setPageIndex(index => Math.max(0, index - 1))}
            disabled={atFirst}
            accessibilityLabel="Previous example"
            accessibilityRole="button"
            accessibilityState={{ disabled: atFirst }}
          >
            <ChevronLeft size={16} color={colors.textSecondary} />
          </TouchableOpacity>
          <Text
            style={styles.paginationText}
            testID="field-text-assist-page"
            accessibilityRole="text"
          >
            {`${safeIndex + 1} of ${pageCount}`}
          </Text>
          <TouchableOpacity
            testID="field-text-assist-next"
            style={[styles.pageButton, atLast && styles.pageButtonDisabled]}
            onPress={() =>
              setPageIndex(index => Math.min(lastIndex, index + 1))
            }
            disabled={atLast}
            accessibilityLabel="Next example"
            accessibilityRole="button"
            accessibilityState={{ disabled: atLast }}
          >
            <ChevronRight size={16} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>
      ) : null}
      {example != null ? (
        <View
          style={[
            styles.exampleCallout,
            showPager ? styles.exampleCalloutLocked : styles.exampleCalloutHug,
          ]}
          testID="field-text-assist-example"
        >
          <View style={styles.exampleHeader}>
            <Text style={styles.exampleLabel} accessibilityRole="header">
              {example.label}
            </Text>
            <TouchableOpacity
              testID="field-text-assist-copy"
              onPress={() => handleCopy(example)}
              style={styles.copyButton}
              accessibilityLabel={`Copy ${example.label} to clipboard`}
              accessibilityRole="button"
              hitSlop={8}
              activeOpacity={0.88}
            >
              {copied ? (
                <Text
                  style={styles.copiedHint}
                  testID="field-text-assist-copied"
                  accessibilityLiveRegion="polite"
                  accessibilityRole="text"
                >
                  Copied
                </Text>
              ) : null}
              <Copy size={16} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>
          <ScrollView
            style={
              showPager ? styles.exampleBodyScroll : styles.exampleBodyScrollHug
            }
            bounces={false}
            nestedScrollEnabled
            testID="field-text-assist-example-scroll"
          >
            <Text style={styles.exampleBody} accessibilityRole="text">
              {example.text}
            </Text>
          </ScrollView>
        </View>
      ) : null}
    </FormFieldOverlay>
  );
};

const styles = StyleSheet.create({
  bulletRow: {
    flexDirection: 'row' as const,
    alignItems: 'flex-start' as const,
    marginBottom: 8,
  },
  bodyLine: {
    flex: 1,
    fontSize: fontSize.body,
    lineHeight: 20,
    color: colors.textSecondary,
  },
  bulletMark: {
    color: colors.textSecondary,
  },
  examplesIntro: {
    fontSize: fontSize.body,
    lineHeight: 20,
    fontWeight: fontWeight.semibold,
    color: colors.textPrimary,
    marginTop: spacing.fieldVerticalGap,
    textAlign: 'center' as const,
    alignSelf: 'stretch' as const,
  },
  pagination: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    gap: spacing.xs,
    marginTop: spacing.fieldVerticalGap,
  },
  pageButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.borderCard,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    backgroundColor: colors.backgroundPrimary,
  },
  pageButtonDisabled: {
    opacity: 0.4,
  },
  paginationText: {
    fontSize: 13,
    fontWeight: fontWeight.semibold,
    color: colors.textSecondary,
    minWidth: 56,
    textAlign: 'center' as const,
  },
  exampleCallout: {
    overflow: 'hidden',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: spacing.cardBorderRadius,
    borderWidth: 1,
    borderColor: colors.borderCard,
    borderLeftWidth: spacing.alertInfoLeftBorder,
    borderLeftColor: colors.textSecondary,
    paddingHorizontal: spacing.cardPadding,
    paddingVertical: spacing.cardPadding,
    marginTop: spacing.fieldVerticalGap,
    marginBottom: spacing.subsectionTitleToFields,
  },
  exampleCalloutLocked: {
    height: FIELD_TEXT_ASSIST_EXAMPLE_CALLOUT_HEIGHT,
  },
  exampleCalloutHug: {
    maxHeight: FIELD_TEXT_ASSIST_EXAMPLE_CALLOUT_HEIGHT,
  },
  exampleBodyScroll: {
    flex: 1,
    ...Platform.select({
      web: {
        overflowY: 'auto' as const,
        overflowX: 'hidden' as const,
      },
      default: {},
    }),
  },
  exampleBodyScrollHug: {
    flexGrow: 0,
    ...Platform.select({
      web: {
        overflowY: 'auto' as const,
        overflowX: 'hidden' as const,
      },
      default: {},
    }),
  },
  exampleHeader: {
    flexDirection: 'row' as const,
    flexWrap: 'nowrap' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    gap: spacing.fieldVerticalGap,
    marginBottom: spacing.fieldVerticalGap,
    ...Platform.select({
      web: { display: 'flex' as const },
      default: {},
    }),
  },
  exampleLabel: {
    flex: 1,
    minWidth: 0,
    fontSize: fontSize.formLabel,
    lineHeight: 20,
    fontWeight: fontWeight.semibold,
    color: colors.textPrimary,
  },
  copyButton: {
    flexDirection: 'row' as const,
    flexWrap: 'nowrap' as const,
    alignItems: 'center' as const,
    gap: spacing.xs,
    flexShrink: 0,
    padding: spacing.infoIconPadding,
    marginTop: -spacing.infoIconPadding,
    marginRight: -spacing.infoIconPadding,
    ...Platform.select({
      web: { display: 'flex' as const },
      default: {},
    }),
  },
  copiedHint: {
    fontSize: fontSize.charCount,
    fontWeight: fontWeight.semibold,
    color: colors.textSecondary,
    ...Platform.select({
      web: {
        display: 'inline' as const,
        width: 'auto' as const,
        whiteSpace: 'nowrap' as const,
      },
      default: {},
    }),
  },
  exampleBody: {
    fontSize: fontSize.body,
    lineHeight: 20,
    color: colors.textPrimary,
  },
  footer: {
    marginTop: 8,
    marginHorizontal: 20,
    marginBottom: 16,
  },
  footerRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: spacing.formControlIconGap,
  },
  footerInsert: {
    flex: 1,
  },
  footerClose: {
    flex: 1,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  },
  closeButton: {
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    alignSelf: 'stretch' as const,
    minHeight: spacing.minimumTapTarget,
    paddingVertical: spacing.formCompactActionPaddingVertical,
    paddingHorizontal: spacing.formCompactActionPaddingHorizontal,
    backgroundColor: 'transparent',
    borderWidth: 0,
    borderColor: 'transparent',
    ...Platform.select({
      web: {
        borderStyle: 'solid' as const,
        borderWidth: 0,
        borderColor: 'transparent',
        border: 'none',
        outlineWidth: 0,
        outlineStyle: 'none' as const,
        boxShadow: 'none' as const,
        appearance: 'none' as const,
        WebkitAppearance: 'none' as const,
        cursor: 'pointer' as const,
        backgroundColor: 'transparent',
      },
      default: {},
    }),
  },
  closeButtonText: {
    color: colors.textPrimary,
    fontWeight: fontWeight.semibold,
    fontSize: fontSize.body,
    textAlign: 'center' as const,
  },
  disabledHint: {
    fontSize: fontSize.charCount,
    color: colors.textSecondary,
    marginTop: spacing.fieldVerticalGap,
    textAlign: 'center' as const,
  },
});
